import GeneEnum from '@/enums/gene.enum';
import Gene from '../../models/gene.model';
import Sapling from '../../models/sapling.model';
import { GeneticsMap, GeneticsMapGroup, WorkChunk } from './models';

export function resultMapsSortingFunction(geneticsMap1: GeneticsMap, geneticsMap2: GeneticsMap): number {
  if (
    geneticsMap1.resultSapling.generationIndex < geneticsMap2.resultSapling.generationIndex ||
    (geneticsMap1.resultSapling.generationIndex === geneticsMap2.resultSapling.generationIndex &&
      (geneticsMap1.chance > geneticsMap2.chance ||
        (geneticsMap1.chance === geneticsMap2.chance &&
          (geneticsMap1.sumOfComposingSaplingsGenerations < geneticsMap2.sumOfComposingSaplingsGenerations ||
            (geneticsMap1.sumOfComposingSaplingsGenerations === geneticsMap2.sumOfComposingSaplingsGenerations &&
              geneticsMap1.crossbreedingSaplings.length < geneticsMap2.crossbreedingSaplings.length)))))
  ) {
    return -1;
  } else {
    return 1;
  }
}

export function resultMapGroupsSortingFunction(
  geneticsMapsGroup1: GeneticsMapGroup,
  geneticsMapsGroup2: GeneticsMapGroup
): number {
  const group1FirstMap = geneticsMapsGroup1.mapList[0];
  const group2FirstMap = geneticsMapsGroup2.mapList[0];
  if (
    group1FirstMap.score > group2FirstMap.score ||
    (group1FirstMap.score === group2FirstMap.score &&
      (group1FirstMap.getChanceProduct() > group2FirstMap.getChanceProduct() ||
        (group1FirstMap.getChanceProduct() === group2FirstMap.getChanceProduct() &&
          (group1FirstMap.resultSapling.generationIndex < group2FirstMap.resultSapling.generationIndex ||
            (group1FirstMap.resultSapling.generationIndex === group2FirstMap.resultSapling.generationIndex &&
              (group1FirstMap.sumOfComposingSaplingsGenerations < group2FirstMap.sumOfComposingSaplingsGenerations ||
                (group1FirstMap.sumOfComposingSaplingsGenerations ===
                  group2FirstMap.sumOfComposingSaplingsGenerations &&
                  geneticsMapsGroup1.resultSaplingGeneString < geneticsMapsGroup2.resultSaplingGeneString)))))))
  ) {
    return -1;
  } else {
    return 1;
  }
}

export function buildInitialSaplingPositions(positionCount: number, withRepetitions: boolean): number[] {
  const positions = [];
  for (let i = 0; i < positionCount; i++) {
    positions.push(withRepetitions ? 0 : i);
  }
  return positions;
}

function rFact(num: number): number {
  if (num === 0) {
    return 1;
  } else {
    return num * rFact(num - 1);
  }
}

export function getMaxPositionsCount(itemsCount: number, withRepetitions: boolean, maxCrossbreedingSaplings: number) {
  return withRepetitions ? maxCrossbreedingSaplings : Math.min(itemsCount, maxCrossbreedingSaplings);
}

export function getNumberOfCrossbreedingCombinations(
  itemsCount: number,
  withRepetitions: boolean,
  minCrossbreedingSaplings: number,
  maxCrossbreedingSaplings: number
) {
  let numberOfAllCombinations = 0;
  const maxItemsInVariation = getMaxPositionsCount(itemsCount, withRepetitions, maxCrossbreedingSaplings);
  for (let k = minCrossbreedingSaplings; k <= maxItemsInVariation; k++) {
    if (withRepetitions) {
      numberOfAllCombinations += rFact(k + itemsCount - 1) / (rFact(k) * rFact(itemsCount - 1));
    } else {
      numberOfAllCombinations += rFact(itemsCount) / (rFact(k) * rFact(itemsCount - k));
    }
  }
  return Math.round(numberOfAllCombinations);
}

export function setNextPosition(
  positions: number[],
  currentPositionIndexForInc: number,
  positionCount: number,
  sourceSaplingsCount: number,
  withRepetitions: boolean,
  mandatorySaplingsCount?: number
): { nextPositionIndexForInc: number; hasMoreCombinations: boolean } {
  let hasMoreCombinations = true;
  let keepOriganizingPositions = true;
  while (keepOriganizingPositions) {
    positions[currentPositionIndexForInc] += 1;

    let maxSaplingIndexOnCurrentPosition;
    if (mandatorySaplingsCount && currentPositionIndexForInc === 0) {
      maxSaplingIndexOnCurrentPosition = mandatorySaplingsCount - 1;
    } else {
      maxSaplingIndexOnCurrentPosition = withRepetitions
        ? sourceSaplingsCount - 1
        : sourceSaplingsCount - (positionCount - currentPositionIndexForInc);
    }

    if (positions[currentPositionIndexForInc] > maxSaplingIndexOnCurrentPosition) {
      if (currentPositionIndexForInc === 0) {
        hasMoreCombinations = false;
        keepOriganizingPositions = false;
      } else {
        currentPositionIndexForInc -= 1;
      }
    } else {
      for (let positionIndex = currentPositionIndexForInc + 1; positionIndex < positions.length; positionIndex++) {
        positions[positionIndex] = positions[positionIndex - 1] + (withRepetitions ? 0 : 1);
      }
      currentPositionIndexForInc = positionCount - 1;
      keepOriganizingPositions = false;
    }
  }

  return {
    nextPositionIndexForInc: currentPositionIndexForInc,
    hasMoreCombinations
  };
}

export function getWorkChunks(
  numberOfWorkChunks: number,
  sourceSaplingsCount: number,
  withRepetitions: boolean,
  minCrossbreedingSaplings: number,
  maxCrossbreedingSaplings: number,
  mandatorySaplingsCount?: number
): WorkChunk[] {
  let allCombinationsCount = getNumberOfCrossbreedingCombinations(
    sourceSaplingsCount,
    withRepetitions,
    minCrossbreedingSaplings,
    maxCrossbreedingSaplings
  );

  if (mandatorySaplingsCount) {
    const combinationsToIgnore = getNumberOfCrossbreedingCombinations(
      sourceSaplingsCount - mandatorySaplingsCount,
      withRepetitions,
      minCrossbreedingSaplings,
      maxCrossbreedingSaplings
    );
    allCombinationsCount -= combinationsToIgnore;
  }

  const combinationsPerWorkChunk = Math.ceil(allCombinationsCount / numberOfWorkChunks);
  const workChunks = [];

  let workChunkIndex = 0;
  let combinationsProcessed = 0;
  for (
    let positionCount = minCrossbreedingSaplings;
    positionCount <= getMaxPositionsCount(sourceSaplingsCount, withRepetitions, maxCrossbreedingSaplings);
    positionCount++
  ) {
    const positions = buildInitialSaplingPositions(positionCount, withRepetitions);

    let positionIndexForInc = positionCount - 1;
    let hasMoreCombinations = true;
    while (hasMoreCombinations) {
      if (combinationsProcessed === 0) {
        workChunks[workChunkIndex] = {
          startingPositions: [...positions],
          combinationsToProcess: 0,
          allCombinationsCount
        };
      }

      const setNextPositionResult = setNextPosition(
        positions,
        positionIndexForInc,
        positionCount,
        sourceSaplingsCount,
        withRepetitions,
        mandatorySaplingsCount
      );
      hasMoreCombinations = setNextPositionResult.hasMoreCombinations;
      positionIndexForInc = setNextPositionResult.nextPositionIndexForInc;

      combinationsProcessed++;

      workChunks[workChunkIndex].combinationsToProcess = combinationsProcessed;
      if (hasMoreCombinations && combinationsProcessed >= combinationsPerWorkChunk) {
        combinationsProcessed = 0;
        workChunkIndex++;
      }
    }
  }

  return workChunks;
}

export function fixSaplingPrototypeAssignments(rawSapling: Sapling): Sapling {
  Object.setPrototypeOf(rawSapling, Sapling.prototype);
  rawSapling.genes.forEach((gene) => {
    Object.setPrototypeOf(gene, Gene.prototype);
  });
  return rawSapling;
}

export function fixPrototypeAssignmentsAfterSerialization(groups: { [key: string]: GeneticsMapGroup }) {
  Object.values(groups).forEach((group) => {
    Object.setPrototypeOf(group, GeneticsMapGroup.prototype);
    group.mapList.forEach((map) => {
      if (Object.getPrototypeOf(map) !== GeneticsMap.prototype) {
        Object.setPrototypeOf(map, GeneticsMap.prototype);
        Object.setPrototypeOf(map.resultSapling, Sapling.prototype);
        map.crossbreedingSaplings.forEach((crossbreedingSapling) => {
          Object.setPrototypeOf(crossbreedingSapling, Sapling.prototype);
        });
        if (map.baseSapling) {
          Object.setPrototypeOf(map.baseSapling, Sapling.prototype);
        }
      }
    });
  });
}

export function linkGenerationTree(groups: { [key: string]: GeneticsMapGroup }) {
  Object.values(groups).forEach((mapGroup) => {
    mapGroup.mapList.forEach((map) => {
      if (map.baseSapling && map.baseSapling.generationIndex > 0) {
        map.baseSaplingVariants = groups[map.baseSapling.toString()];
      }
      map.crossbreedingSaplingsVariants = new Array(map.crossbreedingSaplings.length);
      map.crossbreedingSaplings.forEach((crossbreedingSapling, crossbreedingSaplingIndex) => {
        if (crossbreedingSapling.generationIndex > 0) {
          map.crossbreedingSaplingsVariants![crossbreedingSaplingIndex] = groups[crossbreedingSapling.toString()];
        }
      });
    });
  });
}

export function appendAndOrganizeResults(
  currentGroups: { [key: string]: GeneticsMapGroup },
  newGroups: { [key: string]: GeneticsMapGroup }
): void {
  const keys = Object.keys(newGroups);

  for (let i = 0; i < keys.length; i++) {
    const partialResultKey = keys[i];

    if (Object.prototype.hasOwnProperty.call(currentGroups, partialResultKey)) {
      const currentMapList = currentGroups[partialResultKey].mapList;
      const newMapList = newGroups[partialResultKey].mapList;

      for (let j = 0; j < newMapList.length; j++) {
        currentMapList.push(newMapList[j]);
      }

      currentMapList.sort(resultMapsSortingFunction);

      if (currentMapList.length > 3) {
        currentMapList.length = 3;
      }
    } else {
      currentGroups[partialResultKey] = newGroups[partialResultKey];
    }
  }

  fixPrototypeAssignmentsAfterSerialization(currentGroups);
}

export function createMapGroupMap(mapList: GeneticsMap[]): { [key: string]: GeneticsMapGroup } {
  const mapGroupMap: { [key: string]: GeneticsMapGroup } = {};

  for (let i = 0; i < mapList.length; i++) {
    const geneticsMap = mapList[i];
    const resultSaplingGeneString = geneticsMap.resultSapling.toString();

    let group = mapGroupMap[resultSaplingGeneString];
    if (group === undefined) {
      group = new GeneticsMapGroup(resultSaplingGeneString, [geneticsMap]);
      mapGroupMap[resultSaplingGeneString] = group;
    } else {
      group.mapList.push(geneticsMap);
      group.mapList.sort(resultMapsSortingFunction);

      if (group.mapList.length > 3) {
        group.mapList.length = 3;
      }
    }
  }

  return mapGroupMap;
}

export function getBestSaplingsForNextGeneration(
  sourceSaplings: Sapling[],
  allResults: GeneticsMapGroup[],
  currentGenerationIndex: number,
  numberOfSaplingsAddedBetweenGenerations: number,
  geneScores: Record<GeneEnum, number>
): Sapling[] {
  const resultSaplings: Sapling[] = [];

  const mapsToConsider = allResults
    .filter((mapGroup) => mapGroup.mapList[0].resultSapling.generationIndex === currentGenerationIndex)
    .map((mapGroup) => mapGroup.mapList[0]);

  const resultGeneScoresPerColumn = new Array(6).fill(0);
  const addSaplingScoresToResultGeneScoresPerColumn = (sapling: Sapling) => {
    for (let colIndex = 0; colIndex < resultGeneScoresPerColumn.length; colIndex++) {
      resultGeneScoresPerColumn[colIndex] += geneScores[sapling.genes[colIndex].type];
    }
  };
  sourceSaplings.forEach(addSaplingScoresToResultGeneScoresPerColumn);

  for (
    let saplingsToAdd = Math.min(numberOfSaplingsAddedBetweenGenerations, mapsToConsider.length);
    saplingsToAdd > 0;
    saplingsToAdd--
  ) {
    const resultGeneScoresPerColumnIndexedWorstToBest = resultGeneScoresPerColumn
      .map((score, index) => ({
        score,
        index
      }))
      .sort((a, b) => a.score - b.score);

    let currentSubsetOfMapsToConsider = [...mapsToConsider];
    resultGeneScoresPerColumnIndexedWorstToBest.forEach((colInfo) => {
      const bestScoreInCurrentCol = Math.max(
        ...currentSubsetOfMapsToConsider.map((map) => geneScores[map.resultSapling.genes[colInfo.index].type])
      );
      currentSubsetOfMapsToConsider = currentSubsetOfMapsToConsider.filter(
        (map) => geneScores[map.resultSapling.genes[colInfo.index].type] === bestScoreInCurrentCol
      );
    });
    currentSubsetOfMapsToConsider.sort(resultMapsSortingFunction);

    const bestMapToAdd = currentSubsetOfMapsToConsider[0];
    resultSaplings.push(bestMapToAdd.resultSapling);

    addSaplingScoresToResultGeneScoresPerColumn(bestMapToAdd.resultSapling);

    const indexOfAddedSaplingInCosideredList = mapsToConsider.indexOf(bestMapToAdd);
    mapsToConsider.splice(indexOfAddedSaplingInCosideredList, 1);
  }

  return resultSaplings;
}
